import { ClearOutlined } from "@ant-design/icons";
import { Button } from "antd";
import Title from "antd/es/typography/Title";
import { useFormikContext } from "formik";
import { ExecuteReportValues } from "shared/types";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { ReportFormatEnum, ReportItem } from "shared/api";
import * as Yup from "yup";
import { isXlsxPreviewOnlyReport } from "shared/helpers/reports";
import { useModal } from "shared/hooks";
import { ExecuteReports, ReportsData } from "./ExecuteReportForm";
import { DownloadReport } from "./DownloadReport";

interface ExportReportHeaderProps {
  pdfUrl: string | null;
  selectedReport?: ReportItem;
  executeReports: ExecuteReports;
  isValid: boolean;
  validationSchema?: Yup.AnyObjectSchema;
  getReportData: ReportsData;
  selectedTemplateFormat?: string;
  clearPdfUrl: () => void;
}

const ExportReportHeader: FC<ExportReportHeaderProps> = ({
  pdfUrl,
  selectedReport,
  executeReports,
  isValid,
  validationSchema,
  getReportData,
  clearPdfUrl,
  selectedTemplateFormat,
}) => {
  const { t } = useTranslation("", { keyPrefix: "executeReports" });

  const { values, resetForm } = useFormikContext<ExecuteReportValues>();

  const [
    isVisibleDownloadReportModal,
    showDownloadReportModal,
    hideDownloadReportModal,
  ] = useModal();

  const clearFormAndResetPdfFile = () => {
    resetForm();
    clearPdfUrl();
  };

  const handleDownloadExcel = () => {
    if (pdfUrl && selectedReport) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `${selectedReport.name}.${values.report_format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const isExcelReport =
    values.report_format === "xlsx" || values.report_format === "xls";

  // The preview blob is XLSX for XLSX-preview-only reports, so it can be reused
  // for a direct download only when XLSX is selected; other formats re-fetch.
  const canDownloadFromPreview =
    isExcelReport &&
    (!isXlsxPreviewOnlyReport(selectedReport) ||
      values.report_format === "xlsx");

  return (
    <div className="w-full flex items-center gap-x-4">
      <Title level={3}>
        {t("selectedReport", { name: selectedReport?.name })}
      </Title>

      {values?.id && (
        <ClearOutlined
          onClick={clearFormAndResetPdfFile}
          className="mb-4 self-end !text-red-100 scale-150"
          type="primary"
        />
      )}

      <Button
        onClick={executeReports.handleExecuteReport}
        className="btn-primary--dark min-w-32 !m-0"
        type="primary"
        loading={executeReports.isLoadingReportExport}
        disabled={!isValid || !validationSchema}
      >
        {t("exportReport")}
      </Button>

      <Button
        onClick={getReportData.handleGetReportData}
        className="bg-green-300 w-32"
        type="primary"
        loading={getReportData.isLoadingReportData}
        disabled={!isValid || !validationSchema}
      >
        {t("getData")}
      </Button>

      {pdfUrl && (
        <>
          {canDownloadFromPreview ? (
            <Button
              className="bg-green-100"
              type="primary"
              onClick={handleDownloadExcel}
            >
              {t("downloadFormat", {
                format: values.report_format.toUpperCase(),
              })}
            </Button>
          ) : (
            <Button
              className="bg-green-100"
              type="primary"
              onClick={showDownloadReportModal}
            >
              {t("download")}
            </Button>
          )}

          <Button type="primary" danger onClick={clearFormAndResetPdfFile}>
            {isExcelReport ? t("clearXlsx") : t("clearPdf")}
          </Button>
        </>
      )}

      <DownloadReport
        isOpen={isVisibleDownloadReportModal}
        onClose={hideDownloadReportModal}
        selectedReport={selectedReport}
        selectedTemplateFormat={selectedTemplateFormat}
        initialReportFormat={values.report_format as ReportFormatEnum}
      />
    </div>
  );
};

export { ExportReportHeader };
